/**
 * Calmy accordions: one answer open at a time per group, animated.
 *
 * Covers every accordion built from <details>/<summary> in the theme:
 *   FAQ sections and pages   .cfaq-sec .cfaq__i   panel .cfaq__a
 *   product details          .cfold               panel .cfold__b   (also the tracking FAQ)
 *   Calm Guide FAQ           .ceb-faq__i          panel .ceb-faq__b
 *   materials (homepage)     .cqual__row          panel .cqual__body
 *
 * A GROUP is the items' shared parent list, so separate accordions on one page
 * (product details and the FAQ section below, two FAQ categories) never close
 * each other, and nothing outside these lists - the navigation drawer, the
 * footer columns - is touched.
 *
 * The markup stays native <details>: Enter/Space work, the expanded state is
 * announced, and with this file absent every row still opens on its own. Only
 * the toggle is taken over, so the panel can animate and the open sibling can
 * close. One delegated listener on the document, guarded against a second
 * copy of this file, so theme-editor section reloads never stack handlers.
 *
 * RAPID CLICKS: each panel carries at most one running animation. A new press
 * reads the height the panel is at THIS FRAME, cancels the old animation and
 * runs from there, so a row caught halfway reverses smoothly - and `open` is
 * only cleared when a close actually finishes.
 */
(function () {
  "use strict";
  if (window.__calmyAccordion) return;
  window.__calmyAccordion = true;

  var KINDS = [
    { item: ".cfaq-sec .cfaq__i", panel: ".cfaq__a" },
    { item: ".cfold", panel: ".cfold__b" },
    { item: ".ceb-faq__i", panel: ".ceb-faq__b" },
    { item: ".cqual__row", panel: ".cqual__body" }
  ];
  function kindOf(d) {
    for (var i = 0; i < KINDS.length; i++) if (d.matches(KINDS[i].item)) return KINDS[i];
    return null;
  }

  var DUR_OPEN = 230;
  var DUR_CLOSE = 200;
  var EASE_OPEN = "cubic-bezier(0.2, 0.7, 0.3, 1)";
  var EASE_CLOSE = "cubic-bezier(0.4, 0, 0.6, 1)";

  function reduced() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }
  function panelOf(d) { var k = kindOf(d); return k ? d.querySelector(k.panel) : null; }
  function nowH(p) { return p.getBoundingClientRect().height; }

  /* THE HANDLERS COME OFF BEFORE THE CANCEL, and that order is the whole point.
     cancel() queues its event, so a cancelled animation's oncancel used to run
     AFTER the next one had already been stored - nulling the reference to a
     running animation, which then went uncancelled on the press after that and
     applied its finish (open = false, inline styles cleared) on top of whatever
     the reader had just asked for. Pressed three times quickly, a row ended up
     shut with no animation left to blame. Detaching first means a cancelled
     animation can no longer say anything about a panel it has stopped driving. */
  function stop(p) {
    var a = p.__cfaqAnim;
    if (!a) return;
    p.__cfaqAnim = null;
    a.onfinish = null;
    a.oncancel = null;
    try { a.cancel(); } catch (e) {}
  }
  function settle(p) { p.style.height = ""; p.style.overflow = ""; p.style.opacity = ""; }

  function openRow(d, animate) {
    var p = panelOf(d);
    if (!p) { d.open = true; return; }
    /* Where the panel is right now, before anything is cancelled: a row caught
       mid-collapse opens from the height it had, not from zero. */
    var from = d.open ? nowH(p) : 0;
    stop(p);
    d.classList.remove("is-cfaq-closing");
    d.open = true;
    if (!animate || typeof p.animate !== "function") { settle(p); return; }
    p.style.overflow = "hidden";
    p.style.height = "auto";
    var to = p.scrollHeight;
    p.style.height = from + "px";
    var anim = p.animate(
      [{ height: from + "px", opacity: from ? 1 : 0 }, { height: to + "px", opacity: 1 }],
      { duration: DUR_OPEN, easing: EASE_OPEN }
    );
    p.__cfaqAnim = anim;
    /* Guarded as well as detached: two belts, because a finish that lands on a
       panel some other press now owns is the one thing that can strand a row. */
    anim.onfinish = function () { if (p.__cfaqAnim !== anim) return; p.__cfaqAnim = null; settle(p); };
    anim.oncancel = function () { if (p.__cfaqAnim === anim) p.__cfaqAnim = null; };
  }

  function closeRow(d, animate) {
    var p = panelOf(d);
    if (!d.open) return;
    if (!p) { d.open = false; return; }
    var from = nowH(p);
    stop(p);
    if (!animate || typeof p.animate !== "function") {
      d.open = false; d.classList.remove("is-cfaq-closing"); settle(p); return;
    }
    /* The chevron turns back now rather than in 200ms: the row has to answer
       the press immediately even though the panel is still on its way out. */
    d.classList.add("is-cfaq-closing");
    p.style.overflow = "hidden";
    p.style.height = from + "px";
    var anim = p.animate(
      [{ height: from + "px", opacity: 1 }, { height: "0px", opacity: 0 }],
      { duration: DUR_CLOSE, easing: EASE_CLOSE }
    );
    p.__cfaqAnim = anim;
    anim.onfinish = function () {
      if (p.__cfaqAnim !== anim) return;
      p.__cfaqAnim = null;
      d.open = false;
      d.classList.remove("is-cfaq-closing");
      settle(p);
    };
    anim.oncancel = function () {
      if (p.__cfaqAnim === anim) p.__cfaqAnim = null;
      d.classList.remove("is-cfaq-closing");
    };
  }

  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;
    var sum = e.target.closest("details > summary");
    if (!sum || !sum.parentElement || !kindOf(sum.parentElement)) return;
    /* A link inside a question keeps its own job. */
    var inner = e.target.closest("a, button");
    if (inner && inner !== sum) return;

    /* The native toggle is what has to stop: it would swap the panel in and out
       with no time to animate either way. Focus and scroll position are left
       exactly where the press left them - nothing here calls focus() or
       scrollIntoView(). */
    e.preventDefault();

    var d = sum.parentElement;
    var kind = kindOf(d);
    var animate = !reduced();

    /* Pressing the open row closes it, so a section can end up with nothing
       open - that is the state a reader asked for. */
    if (d.open && !d.classList.contains("is-cfaq-closing")) { closeRow(d, animate); return; }

    /* ONE AT A TIME, ONLY WITHIN THIS GROUP: the open siblings of the same
       kind in the same list. */
    Array.prototype.forEach.call(d.parentElement.children, function (o) {
      if (o !== d && o.tagName === "DETAILS" && o.open && kindOf(o) === kind) closeRow(o, animate);
    });
    openRow(d, animate);
  });

  /* The class only tells the stylesheet that the panel is animated from here,
     so its CSS open keyframe - the no-script fallback - stands down. */
  function mark(root) {
    (root || document).querySelectorAll(".cfaq-sec").forEach(function (s) {
      s.classList.add("is-cfaqjs");
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { mark(); });
  else mark();
  document.addEventListener("shopify:section:load", function (e) { mark(e.target); });
})();
